import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StyleSheet, Pressable } from 'react-native';
import Animated, { interpolate, useAnimatedStyle, withTiming, Extrapolation, } from 'react-native-reanimated';
import { ScrollView } from 'react-native-gesture-handler';
import { useQueryClient } from '@tanstack/react-query';
import { withTheme } from '@rneui/themed';
import 'shaka-player/dist/controls.css';
import { PlaybackEvent, PlaybackEventEmitter, PlaybackState, analytics, onAVStarted, useContentPlaybackState, } from '@annunciate/core';
import { useAnimatedVideoStyle, useAnimatedUtilityStyles, useIsPlayerMinimized, View, Row, Info, PlayButton, ContentBody, useWindowWidth, playerRef, IconButton, useProgressUpdater, usePlayPauseHelpers, } from '@annunciate/ui';
import { DiscussionChannelAware } from '@annunciate/comments';
import { getContentProgress } from '@annunciate/query';
import { getProgressTime } from '@annunciate/ui/dist/utils/progress';
import { Container } from '@annunciate/ui/dist/components/shared';
import { ShakaPlayer } from './ShakaPlayer';
const UnthemedVideoPlayer = ({ playing, name, position, miniplayerHeight, miniplayerContainer = {}, metaContainer = {}, infoContainer = {}, buttonContainer = {}, playButtonStyle = {}, playButtonContainer = {}, }) => {
    const player = useRef(null);
    const { bodyOpacityStyle, } = useAnimatedUtilityStyles(position);
    const { rotationStyle, miniplayerOpacityStyle, } = useAnimatedVideoStyle({ position });
    const isMinimized = useIsPlayerMinimized();
    const client = useQueryClient();
    const winWidth = useWindowWidth();
    const { state: playbackState, setState: setPlaybackState, getState: getPlaybackState, } = useContentPlaybackState(playing?.id);
    const initiallyPausedRef = useRef(getPlaybackState(playing?.id || '') === PlaybackState.STOPPED);
    const { onPause, onUnpause, } = usePlayPauseHelpers();
    const [isHover, setIsHover] = useState(false);
    const backControlStyle = useAnimatedStyle(() => {
        const opacity = interpolate(position.value, [0, 1], [-1, 1], Extrapolation.CLAMP);
        return {
            opacity: isHover ? opacity : withTiming(0, {
                duration: 500,
            }),
        };
    }, [isHover]);
    const onPauseWrapped = useCallback(async () => {
        initiallyPausedRef.current = true;
        player.current.pause();
        onPause(playing.id);
    }, [onPause, playing.id]);
    const onUnpauseWrapped = useCallback(async () => {
        initiallyPausedRef.current = false;
        const position = onUnpause(playing.id);
        player.current.play(position);
    }, [onUnpause, playing.id]);
    useEffect(() => {
        PlaybackEventEmitter.on(PlaybackEvent.VIDEO_PAUSE, onPauseWrapped);
        PlaybackEventEmitter.on(PlaybackEvent.VIDEO_UNPAUSE, onUnpauseWrapped);
        return () => {
            PlaybackEventEmitter.off(PlaybackEvent.VIDEO_PAUSE, onPauseWrapped);
            PlaybackEventEmitter.off(PlaybackEvent.VIDEO_UNPAUSE, onUnpauseWrapped);
        };
    }, [onPauseWrapped, onUnpauseWrapped]);
    const updateProgress = useProgressUpdater(playing);
    const getInitialPosition = useCallback(async (contentId) => {
        const progress = getContentProgress(client, contentId);
        let position = 0;
        if (progress && progress.percentage !== 100) {
            position = getProgressTime(progress);
        }
        return position;
    }, [client]);
    useEffect(() => {
        const interval = setInterval(() => {
            if (player.current && playbackState === PlaybackState.PLAYING) {
                const position = player.current.getPosition();
                updateProgress(position);
                analytics.emitContentEvent("heartbeat", { content: playing, position });
            }
        }, 2000);
        return () => {
            clearInterval(interval);
        };
    }, [playing, updateProgress, playbackState]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Animated.View, { style: [
                miniplayerOpacityStyle,
                {
                    width: winWidth - ((miniplayerHeight * 16) / 9),
                    alignSelf: 'flex-end',
                },
            ] },
            React.createElement(View, { style: miniplayerContainer },
                React.createElement(Row, { style: metaContainer },
                    React.createElement(View, { style: infoContainer },
                        React.createElement(Info, { name: name, onPress: () => playerRef.current?.expand({ duration: 500 }) })),
                    React.createElement(View, { style: buttonContainer },
                        React.createElement(PlayButton, { playIconName: 'play', pauseIconName: 'pause', iconStyle: playButtonStyle, containerStyle: playButtonContainer }))))),
        React.createElement(ScrollView, { style: {
                height: '100%',
                backgroundColor: 'transparent',
                overflow: isMinimized ? 'hidden' : undefined,
            } },
            React.createElement(Pressable, { style: { display: 'flex' }, onHoverIn: () => setIsHover(true), onHoverOut: () => setIsHover(false) },
                React.createElement(Animated.View, { style: rotationStyle },
                    React.createElement(ShakaPlayer, { ref: player, autoPlay: !initiallyPausedRef.current, displayControls: !isMinimized, src: playing, initialPosition: async ({ id }) => {
                            return await getInitialPosition(id);
                        }, onTrackLoadRequested: (content, position) => {
                            onAVStarted(content.id, position);
                            analytics.emitContentEvent("started", {
                                content,
                                position,
                            });
                        }, onTrackLoading: ({ id }) => {
                            setPlaybackState(PlaybackState.LOADING, id);
                        }, onTrackBuffering: ({ id }) => {
                            const oldState = getPlaybackState();
                            if (oldState === PlaybackState.PLAYING) {
                                setPlaybackState(PlaybackState.LOADING, id);
                            }
                        }, onTrackLoaded: ({ id }) => {
                            const newState = initiallyPausedRef.current
                                ? PlaybackState.STOPPED
                                : PlaybackState.PLAYING;
                            setPlaybackState(newState, id);
                        }, onTrackPlaying: (content, position) => {
                            const oldState = getPlaybackState();
                            if (oldState !== PlaybackState.LOADING) {
                                onAVStarted(content.id, position, true);
                                analytics.emitPlaybackEvent("resumed", {
                                    content_asset_id: String(content.id),
                                    position,
                                });
                                analytics.emitContentEvent("playing", {
                                    content,
                                    position,
                                });
                            }
                            setPlaybackState(PlaybackState.PLAYING, content.id);
                        }, onTrackPaused: (content) => {
                            const position = player.current?.getPosition();
                            analytics.emitContentEvent("pausing", { content, position });
                            analytics.emitPlaybackEvent("paused", {
                                content_asset_id: String(content.id),
                                position,
                            });
                            setPlaybackState(PlaybackState.STOPPED, content.id);
                        }, onTrackEnded: (content) => {
                            const position = player.current?.getPosition();
                            analytics.emitContentEvent("completed", { content, position });
                            PlaybackEventEmitter.emit(PlaybackEvent.VIDEO_ENDED, content.id);
                            setPlaybackState(PlaybackState.NONE, content.id);
                        } })),
                React.createElement(Animated.View, { id: "video-back-button", style: [
                        staticStyles.back,
                        backControlStyle,
                    ] },
                    React.createElement(IconButton, { containerStyle: {
                            borderRadius: 99999,
                            paddingLeft: 17,
                            paddingRight: 20,
                            paddingTop: 11,
                            paddingBottom: 11,
                            alignItems: 'flex-start',
                        }, style: {
                            color: 'white',
                            textShadowColor: 'black',
                            textShadowRadius: 5,
                        }, activeIconName: "chevron-left", onPress: () => {
                            playerRef.current?.collapse({ duration: 500 });
                        } }))),
            React.createElement(Animated.View, { style: bodyOpacityStyle }, !isMinimized && (React.createElement(React.Fragment, null,
                React.createElement(ContentBody, { content: playing }),
                React.createElement(Container, null,
                    React.createElement(DiscussionChannelAware, { id: playing.id, type: "content" }))))))));
};
const staticStyles = StyleSheet.create({
    back: {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: 60,
        zIndex: 1,
        backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(255,255,255,0) 100%)',
    },
});
export const VideoPlayer = withTheme(UnthemedVideoPlayer, 'VideoPlayer');
//# sourceMappingURL=VideoPlayer.js.map